import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Library, Users, Settings, Plus, LogOut, Search, Filter, X, Clock, Share2, Tag, Bot, Sparkles, BookOpen, BarChart3 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import PromptCard from '../components/PromptCard';
import CreatePromptModal from '../components/CreatePromptModal';
import ConnectionsTab from '../components/ConnectionsTab';
import SettingsTab from '../components/SettingsTab';
import SharedPromptsTab from '../components/SharedPromptsTab';
import TemplatesTab from '../components/TemplatesTab';
import AISuggestModal from '../components/AISuggestModal';
import AnalyticsTab from '../components/AnalyticsTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = async () => {
  if (typeof chrome !== 'undefined' && chrome.storage) {
    const { accessToken } = await chrome.storage.local.get('accessToken');
    if (accessToken && accessToken !== 'session_active') {
      return { Authorization: `Bearer ${accessToken}` };
    }
  }
  return {};
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('prompts');
  const [prompts, setPrompts] = useState([]);
  const [filteredPrompts, setFilteredPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTag, setFilterTag] = useState(null);
  const [filterType, setFilterType] = useState(null); // 'function', 'category', 'model'
  const [showAISuggest, setShowAISuggest] = useState(false);

  useEffect(() => {
    if (activeTab === 'prompts' && user) {
      fetchPrompts();
    }
  }, [activeTab, user]);

  useEffect(() => {
    applyFilters();
  }, [prompts, searchQuery, filterTag]);

  const fetchPrompts = async (retryCount = 0) => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const { data } = await axios.get(`${API_URL}/api/prompts`, {
        withCredentials: true,
        headers
      });
      setPrompts(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching prompts:', error);
      
      // Retry once on first 401 error (cookies might be setting)
      if (error.response?.status === 401 && retryCount === 0) {
        setTimeout(() => fetchPrompts(1), 1000);
        return;
      }
      
      // Show error after retry or on other errors
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else {
        toast.error('Failed to load prompts. Please refresh the page.');
      }
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...prompts];

    if (searchQuery) {
      filtered = filtered.filter(p =>
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.content.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (filterTag) {
      if (filterType === 'function') {
        filtered = filtered.filter(p => p.function_tag === filterTag);
      } else if (filterType === 'category') {
        filtered = filtered.filter(p => p.category_tag === filterTag);
      } else if (filterType === 'model') {
        filtered = filtered.filter(p => p.model_tag === filterTag);
      }
    }

    setFilteredPrompts(filtered);
  };

  const handleLogout = async () => {
    await logout();
  };

  const setFilter = (type, tag) => {
    if (filterTag === tag && filterType === type) {
      setFilterTag(null);
      setFilterType(null);
    } else {
      setFilterTag(tag);
      setFilterType(type);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterTag(null);
    setFilterType(null);
  };

  return (
    <div className="w-[400px] h-[600px] flex flex-col bg-[#FAFAF9]" data-testid="dashboard">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-[#E7E5E4] sticky top-0 z-50 px-4 py-3 flex items-center justify-between">
        <h1 className="text-lg tracking-tight font-semibold text-[#1C1917]">CollabPrompt</h1>
        <button
          onClick={handleLogout}
          className="text-[#78716C] hover:text-[#1C1917] transition-colors"
          title="Logout"
          data-testid="logout-button"
        >
          <LogOut size={18} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 rounded-lg bg-[#F5F2EA] p-1 mx-4 mt-3">
        <button
          onClick={() => setActiveTab('prompts')}
          className={`flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md transition-colors ${
            activeTab === 'prompts'
              ? 'bg-white text-[#1C1917] shadow-sm'
              : 'text-[#78716C] hover:text-[#1C1917]'
          }`}
          data-testid="prompts-tab"
        >
          <Library size={12} />
          <span>Library</span>
        </button>
        <button
          onClick={() => setActiveTab('templates')}
          className={`flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md transition-colors ${
            activeTab === 'templates'
              ? 'bg-white text-[#1C1917] shadow-sm'
              : 'text-[#78716C] hover:text-[#1C1917]'
          }`}
          data-testid="templates-tab"
        >
          <BookOpen size={12} />
          <span>Templates</span>
        </button>
        <button
          onClick={() => setActiveTab('shared')}
          className={`flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md transition-colors ${
            activeTab === 'shared'
              ? 'bg-white text-[#1C1917] shadow-sm'
              : 'text-[#78716C] hover:text-[#1C1917]'
          }`}
          data-testid="shared-tab"
        >
          <Share2 size={12} />
          <span>Shared</span>
        </button>
        <button
          onClick={() => setActiveTab('connections')}
          className={`flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md whitespace-nowrap transition-colors ${
            activeTab === 'connections'
              ? 'bg-white text-[#1C1917] shadow-sm'
              : 'text-[#78716C] hover:text-[#1C1917]'
          }`}
          data-testid="connections-tab"
        >
          <Users size={12} />
          <span>People</span>
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          className={`flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md whitespace-nowrap transition-colors ${
            activeTab === 'analytics'
              ? 'bg-white text-[#1C1917] shadow-sm'
              : 'text-[#78716C] hover:text-[#1C1917]'
          }`}
          data-testid="analytics-tab"
        >
          <BarChart3 size={12} />
          <span>Analytics</span>
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          className={`flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md whitespace-nowrap transition-colors ${
            activeTab === 'settings'
              ? 'bg-white text-[#1C1917] shadow-sm'
              : 'text-[#78716C] hover:text-[#1C1917]'
          }`}
          data-testid="settings-tab"
        >
          <Settings size={12} />
          <span>Settings</span>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {activeTab === 'prompts' && (
          <>
            {/* Search and Filters */}
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#78716C]" size={16} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search prompts..."
                  className="rounded-lg border border-[#E7E5E4] bg-white pl-10 pr-3 py-2 text-sm text-[#1C1917] placeholder-[#78716C] focus:border-[#2C4C3B] focus:ring-1 focus:ring-[#2C4C3B] focus:outline-none w-full"
                  data-testid="search-prompts-input"
                />
              </div>

              {/* Quick Filters */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setFilter('function', 'Sales')}
                  className={`inline-flex items-center rounded-md px-2 py-1 text-[10px] font-medium tracking-wide uppercase transition-colors ${
                    filterTag === 'Sales' && filterType === 'function'
                      ? 'bg-[#2C4C3B] text-white'
                      : 'bg-[#EAF0EC] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white'
                  }`}
                  data-testid="filter-sales"
                >
                  <Tag size={10} className="mr-1" />
                  Sales
                </button>
                <button
                  onClick={() => setFilter('function', 'Marketing')}
                  className={`inline-flex items-center rounded-md px-2 py-1 text-[10px] font-medium tracking-wide uppercase transition-colors ${
                    filterTag === 'Marketing' && filterType === 'function'
                      ? 'bg-[#2C4C3B] text-white'
                      : 'bg-[#EAF0EC] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white'
                  }`}
                  data-testid="filter-marketing"
                >
                  <Tag size={10} className="mr-1" />
                  Marketing
                </button>
                <button
                  onClick={() => setFilter('model', 'ChatGPT')}
                  className={`inline-flex items-center rounded-md px-2 py-1 text-[10px] font-medium tracking-wide uppercase transition-colors ${
                    filterTag === 'ChatGPT' && filterType === 'model'
                      ? 'bg-[#2C4C3B] text-white'
                      : 'bg-[#F5F2EA] text-[#78716C] hover:bg-[#2C4C3B] hover:text-white'
                  }`}
                  data-testid="filter-chatgpt"
                >
                  <Bot size={10} className="mr-1" />
                  ChatGPT
                </button>
                <button
                  onClick={() => setFilter('model', 'Claude')}
                  className={`inline-flex items-center rounded-md px-2 py-1 text-[10px] font-medium tracking-wide uppercase transition-colors ${
                    filterTag === 'Claude' && filterType === 'model'
                      ? 'bg-[#2C4C3B] text-white'
                      : 'bg-[#F5F2EA] text-[#78716C] hover:bg-[#2C4C3B] hover:text-white'
                  }`}
                  data-testid="filter-claude"
                >
                  <Bot size={10} className="mr-1" />
                  Claude
                </button>
                {(searchQuery || filterTag) && (
                  <button
                    onClick={clearFilters}
                    className="inline-flex items-center rounded-md px-2 py-1 text-[10px] font-medium tracking-wide uppercase bg-[#E27D60] text-white hover:bg-[#D36C50] transition-colors"
                    data-testid="clear-filters"
                  >
                    <X size={10} className="mr-1" />
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Prompts List */}
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2C4C3B] mx-auto"></div>
              </div>
            ) : filteredPrompts.length === 0 ? (
              <div className="text-center py-8" data-testid="empty-state">
                <Library size={48} className="mx-auto text-[#E7E5E4] mb-3" />
                <p className="text-sm text-[#78716C]">
                  {prompts.length === 0 ? 'No prompts yet. Create your first one!' : 'No prompts match your filters'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredPrompts.map((prompt) => (
                  <PromptCard key={prompt.prompt_id} prompt={prompt} onUpdate={fetchPrompts} />
                ))}
              </div>
            )}

            {/* Add Buttons */}
            <div className="fixed bottom-6 right-6 flex flex-col gap-2">
              <button
                onClick={() => setShowAISuggest(true)}
                className="inline-flex items-center justify-center rounded-full p-3 bg-[#E27D60] text-white shadow-lg hover:bg-[#D36C50] transition-colors active:scale-95"
                data-testid="ai-suggest-button"
                title="AI Suggestions"
              >
                <Sparkles size={20} />
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center justify-center rounded-full p-4 bg-[#2C4C3B] text-white shadow-lg hover:bg-[#243D30] transition-colors active:scale-95"
                data-testid="add-prompt-button"
              >
                <Plus size={24} />
              </button>
            </div>
          </>
        )}

        {activeTab === 'templates' && <TemplatesTab onUseTemplate={() => { setActiveTab('prompts'); fetchPrompts(); }} />}
        {activeTab === 'shared' && <SharedPromptsTab />}
        {activeTab === 'connections' && <ConnectionsTab />}
        {activeTab === 'analytics' && <AnalyticsTab />}
        {activeTab === 'settings' && <SettingsTab />}
      </div>

      {showCreateModal && (
        <CreatePromptModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchPrompts();
          }}
        />
      )}

      {showAISuggest && (
        <AISuggestModal
          onClose={() => setShowAISuggest(false)}
          onSelectSuggestion={(suggestion) => {
            setShowAISuggest(false);
            setShowCreateModal(true);
            // Pre-fill with suggestion - we'll update CreatePromptModal to accept initialData
          }}
          existingPromptsCount={prompts.length}
        />
      )}
    </div>
  );
}